/**
 * Fundus field-of-view (FOV) detection and circular cropping.
 *
 * Fundus captures put a roughly circular illuminated retina (the FOV) on a
 * black surround. Instead of assuming the FOV is centred and clipping with a
 * fixed CSS circle (which can crop the FOV on off-centre or non-square
 * captures), this detects the actual FOV circle from the pixels and clips
 * exactly along it.
 *
 * Detection is robust to noise/glare and to "mostly black" images because it
 * uses the LARGEST connected bright component (not a raw bounding box of every
 * bright pixel). A small inward radius margin trims the near-black FOV rim so
 * the report renders a clean retinal circle with no dark outline, and a guard
 * skips clipping entirely for full-frame images that have no circular FOV.
 */

const DETECT_MAX = 400; // longest edge used for (fast) detection
const OUTPUT_MAX = 640; // longest edge of the produced circular image
const SURROUND_THRESHOLD = 12; // luminance at/below this counts as black surround
// Clip just INSIDE the detected FOV edge. The detected boundary sits at the
// near-black surround (luminance ~12), a few percent beyond the visible retina;
// padding outward (>1) re-included that dark annulus and drew a black ring
// around every image in the report. 0.99 shaves that rim without cropping any
// visible retina (the illuminated retina ends well inside the boundary).
const RADIUS_MARGIN = 0.99;

const loadImage = src =>
  new Promise((resolve, reject) => {
    const img = new window.Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => resolve(img);
    img.onerror = () =>
      reject(new Error(`Failed to load the image from URL: ${src}`));
    img.src = src;
  });

/**
 * Detects the fundus FOV circle in (downscaled) image data.
 *
 * Strategy:
 *  1. Detect the SURROUND (near-black mask) by flood-filling inward from the
 *     image edges. Keying on the surround (consistently ~0) instead of FOV
 *     brightness is what lets this work on very dark captures.
 *  2. Take the largest connected component of NON-surround pixels = the FOV.
 *  3. Fit the circle from the FOV's WIDEST row and column (its equator),
 *     NOT its bounding box. The equator lives deep in the interior, which the
 *     surround flood can never reach, so it's detected reliably even when the
 *     rim is so dark it gets swallowed by the surround. (A bounding box, by
 *     contrast, mis-sizes/mis-centres the circle whenever a dark rim segment
 *     is flooded away - which is what clipped the bright crescent before.)
 *
 * Returns { cx, cy, r } in downscaled coordinates, or null if there's no black
 * border (full-frame image) or no meaningful FOV region.
 */
function detectFovCircle(data, w, h) {
  const luminance = idx =>
    0.299 * data[idx * 4] +
    0.587 * data[idx * 4 + 1] +
    0.114 * data[idx * 4 + 2];

  // --- 1. flood the near-black surround inward from the edges ---
  const surround = new Uint8Array(w * h);
  const queue = new Int32Array(w * h);
  let head = 0;
  let tail = 0;
  const seedSurround = pos => {
    if (!surround[pos] && luminance(pos) <= SURROUND_THRESHOLD) {
      surround[pos] = 1;
      queue[tail++] = pos;
    }
  };
  for (let x = 0; x < w; x++) {
    seedSurround(x);
    seedSurround((h - 1) * w + x);
  }
  for (let y = 0; y < h; y++) {
    seedSurround(y * w);
    seedSurround(y * w + w - 1);
  }
  while (head < tail) {
    const pos = queue[head++];
    const x = pos % w;
    const y = (pos - x) / w;
    if (x > 0) seedSurround(pos - 1);
    if (x < w - 1) seedSurround(pos + 1);
    if (y > 0) seedSurround(pos - w);
    if (y < h - 1) seedSurround(pos + w);
  }

  // If almost nothing flooded, there's no black border -> no circular FOV.
  let surroundCount = 0;
  for (let i = 0; i < w * h; i++) if (surround[i]) surroundCount++;
  if (surroundCount < w * h * 0.02) return null;

  // --- 2. largest connected component of the NON-surround pixels = the FOV ---
  const visited = new Uint8Array(w * h);
  let bestSize = 0;
  let bestStart = -1;
  for (let start = 0; start < w * h; start++) {
    if (visited[start] || surround[start]) continue;
    head = 0;
    tail = 0;
    queue[tail++] = start;
    visited[start] = 1;
    let size = 0;
    while (head < tail) {
      const pos = queue[head++];
      const x = pos % w;
      const y = (pos - x) / w;
      size++;
      if (x > 0 && !visited[pos - 1] && !surround[pos - 1]) {
        visited[pos - 1] = 1;
        queue[tail++] = pos - 1;
      }
      if (x < w - 1 && !visited[pos + 1] && !surround[pos + 1]) {
        visited[pos + 1] = 1;
        queue[tail++] = pos + 1;
      }
      if (y > 0 && !visited[pos - w] && !surround[pos - w]) {
        visited[pos - w] = 1;
        queue[tail++] = pos - w;
      }
      if (y < h - 1 && !visited[pos + w] && !surround[pos + w]) {
        visited[pos + w] = 1;
        queue[tail++] = pos + w;
      }
    }
    if (size > bestSize) {
      bestSize = size;
      bestStart = start;
    }
  }
  if (bestStart < 0 || bestSize < w * h * 0.01) return null;

  // Re-flood just the winning component into its own mask.
  const comp = new Uint8Array(w * h);
  head = 0;
  tail = 0;
  comp[bestStart] = 1;
  queue[tail++] = bestStart;
  while (head < tail) {
    const pos = queue[head++];
    const x = pos % w;
    const y = (pos - x) / w;
    if (x > 0 && !comp[pos - 1] && !surround[pos - 1]) {
      comp[pos - 1] = 1;
      queue[tail++] = pos - 1;
    }
    if (x < w - 1 && !comp[pos + 1] && !surround[pos + 1]) {
      comp[pos + 1] = 1;
      queue[tail++] = pos + 1;
    }
    if (y > 0 && !comp[pos - w] && !surround[pos - w]) {
      comp[pos - w] = 1;
      queue[tail++] = pos - w;
    }
    if (y < h - 1 && !comp[pos + w] && !surround[pos + w]) {
      comp[pos + w] = 1;
      queue[tail++] = pos + w;
    }
  }

  // --- 3. fit the circle from the widest row/column (the equator) ---
  // Per-row horizontal span.
  let maxRowSpan = 0;
  for (let y = 0; y < h; y++) {
    const base = y * w;
    let l = -1;
    let rr = -1;
    for (let x = 0; x < w; x++) {
      if (comp[base + x]) {
        if (l < 0) l = x;
        rr = x;
      }
    }
    if (l >= 0 && rr - l + 1 > maxRowSpan) maxRowSpan = rr - l + 1;
  }
  // Average the rows whose span is near-max to get a stable centre.
  const rowThresh = maxRowSpan * 0.95;
  let sumY = 0;
  let sumMidX = 0;
  let cnt = 0;
  for (let y = 0; y < h; y++) {
    const base = y * w;
    let l = -1;
    let rr = -1;
    for (let x = 0; x < w; x++) {
      if (comp[base + x]) {
        if (l < 0) l = x;
        rr = x;
      }
    }
    if (l >= 0 && rr - l + 1 >= rowThresh) {
      sumY += y;
      sumMidX += (l + rr) / 2;
      cnt++;
    }
  }
  if (cnt === 0) return null;

  // Per-column vertical span (so the diameter is right even if one axis is
  // partially truncated by the surround flood).
  let maxColSpan = 0;
  for (let x = 0; x < w; x++) {
    let t = -1;
    let b = -1;
    for (let y = 0; y < h; y++) {
      if (comp[y * w + x]) {
        if (t < 0) t = y;
        b = y;
      }
    }
    if (t >= 0 && b - t + 1 > maxColSpan) maxColSpan = b - t + 1;
  }

  return {
    cx: sumMidX / cnt,
    cy: sumY / cnt,
    r: Math.max(maxRowSpan, maxColSpan) / 2
  };
}

/**
 * Detects the fundus FOV circle and returns a square PNG data URL containing
 * just that circle (transparent outside it). Falls back to the original url if
 * the image can't be processed or has no detectable circular FOV.
 *
 * @param {string} url  source image (data URL or remote URL)
 * @returns {Promise<string>} a PNG data URL, or the original url
 */
export async function clipFundusToFov(url) {
  let objectUrl;
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    objectUrl = URL.createObjectURL(blob);
    const img = await loadImage(objectUrl);

    const W = img.width;
    const H = img.height;
    if (!W || !H) return url;

    // --- detect on a downscaled copy (fast, noise-tolerant) ---
    const detScale = Math.min(1, DETECT_MAX / Math.max(W, H));
    const dw = Math.max(1, Math.round(W * detScale));
    const dh = Math.max(1, Math.round(H * detScale));
    const dCanvas = document.createElement("canvas");
    dCanvas.width = dw;
    dCanvas.height = dh;
    const dCtx = dCanvas.getContext("2d");
    dCtx.drawImage(img, 0, 0, dw, dh);
    const dData = dCtx.getImageData(0, 0, dw, dh).data;

    const circle = detectFovCircle(dData, dw, dh);
    if (!circle) return url;

    // Map the detected circle back to full-resolution coordinates.
    const invScale = 1 / detScale;
    const cx = circle.cx * invScale;
    const cy = circle.cy * invScale;
    let r = circle.r * invScale * RADIUS_MARGIN;
    // Never let the circle extend past the image, or the crop produces flat
    // edges where there are no source pixels.
    r = Math.min(r, cx, cy, W - cx, H - cy);
    const diameter = 2 * r;

    // --- render the circle to a square output canvas ---
    const outScale = Math.min(1, OUTPUT_MAX / diameter);
    const outSize = Math.max(1, Math.round(diameter * outScale));
    const out = document.createElement("canvas");
    out.width = outSize;
    out.height = outSize;
    const oCtx = out.getContext("2d");

    oCtx.save();
    oCtx.beginPath();
    oCtx.arc(outSize / 2, outSize / 2, outSize / 2, 0, Math.PI * 2);
    oCtx.closePath();
    oCtx.clip();

    // The crop square is [cx-r, cy-r, diameter]; clamp the SOURCE rect to the
    // image and map it to the matching sub-region of the output, so a circle
    // near an edge draws correctly (no proportional-clip distortion).
    const sx = cx - r;
    const sy = cy - r;
    const sx0 = Math.max(0, sx);
    const sy0 = Math.max(0, sy);
    const sx1 = Math.min(W, sx + diameter);
    const sy1 = Math.min(H, sy + diameter);
    const dStep = outSize / diameter;
    oCtx.drawImage(
      img,
      sx0,
      sy0,
      sx1 - sx0,
      sy1 - sy0,
      (sx0 - sx) * dStep,
      (sy0 - sy) * dStep,
      (sx1 - sx0) * dStep,
      (sy1 - sy0) * dStep
    );
    oCtx.restore();

    return out.toDataURL("image/png");
  } catch (error) {
    return url; // fall back to the unprocessed image
  } finally {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }
}
